import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { useAuth } from "./stores/auth";

// Routes the `personal` account kind has no business in — driver/staff/
// client/corporate management, live fleet tracking, chat, and the internal
// support inbox (all customers' contact messages). Deliveries/vehicles/
// wallet/transactions/analytics ARE allowed, scoped to their own data
// server-side (see routes/parcels.rs, routes/ops.rs).
const PERSONAL_BLOCKED_PREFIXES = [
  "/app/tracking",
  "/app/users",
  "/app/chat",
  "/app/support",
  "/app/dispatch",
  "/app/jobs",
];

// The support inbox is staff-only server-side (require_staff_claims);
// mirror that here so non-staff never see a dead link that 403s.
const STAFF_ONLY_PREFIXES = ["/app/support"];

// Personal/staff rosters, chat, trackers are staff management tools — a
// corporate login has no legitimate reason to browse them and the backing
// list endpoints are staff-only server-side anyway. Clients IS allowed —
// list_clients/create_client/update_client scope a corporate caller to its
// own contacts server-side (see routes/ops.rs). Deliveries/vehicles/
// drivers/analytics also allowed, scoped the same way.
const CORPORATE_BLOCKED_PREFIXES = [
  "/app/users/personal",
  "/app/users/staff",
  "/app/chat",
  "/app/support",
  "/app/trackers",
  "/app/dispatch",
  "/app/jobs",
];
// Exact-match only — /app/users/corporates/:id is their own company
// dashboard (Team tab etc.), not the staff-only directory listing.
const CORPORATE_BLOCKED_EXACT = ["/app/users/corporates"];

// `client` kind accounts are minted automatically by OTP login for any
// unused email/phone (self-signup, not staff-invited) — treat them like
// `personal` plus no admin/chat/trackers surface at all. Deliveries/
// vehicles/wallet/tracking/settings ARE allowed, scoped to their own
// client_id server-side (see routes/parcels.rs, routes/vehicles.rs).
const CLIENT_BLOCKED_PREFIXES = [
  "/app/chat",
  "/app/users",
  "/app/support",
  "/app/trackers",
  "/app/analytics",
  "/app/dispatch",
  "/app/jobs",
];

// Dispatch board is for dispatch-capable staff only (admin/superadmin/
// dispatch_officer) — a driver-only staff login (ops::is_driver_only) gets
// /app/jobs instead, the self-serve accept/decline queue for its own offers.
const DISPATCH_CAPABLE_ROLES = ["superadmin", "admin", "dispatch_officer"];

// A driver-only staff login (every role === "driver") is scoped server-side
// to its own parcels/vehicle (see is_driver_only in backend/src/auth/
// middleware.rs) but has no legitimate reason to browse the user
// directories, platform analytics, the internal support inbox, or the
// fleet-wide live-tracking map (which also lists every other driver) —
// mirror those backend denials here so a driver never hits a dead 403 link.
const DRIVER_ONLY_BLOCKED_PREFIXES = [
  "/app/tracking",
  "/app/users",
  "/app/analytics",
  "/app/support",
  "/app/trackers",
  "/app/vehicles/assignments",
];
// Matched separately (not a prefix) since /app/vehicles/:id itself stays
// open for driver-only — only the live-tracking sub-route is blocked.
const DRIVER_ONLY_BLOCKED_SUFFIXES = ["/track"];

// Superadmin-only server-side (require_superadmin_claims, routes/trackers.rs).
const SUPERADMIN_ONLY_PREFIXES = ["/app/trackers"];

// Layouts
import AuthLayout from "./layouts/AuthLayout";
import AppLayout from "./layouts/AppLayout";

// Auth Pages
import LoginPage from "./pages/auth/Login";
import RegisterPage from "./pages/auth/Register";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ResetPassword from "./pages/auth/ResetPassword";
import LinkedinCallback from "./pages/auth/LinkedinCallback";
import GoogleCallback from "./pages/auth/GoogleCallback";
import MicrosoftCallback from "./pages/auth/MicrosoftCallback";
import Handoff from "./pages/auth/Handoff";
import ConfirmEmail from "./pages/auth/ConfirmEmail";
import Activate from "./pages/auth/Activate";

// App Pages
import Dashboard from "./pages/app/Dashboard";
import Wallet from "./pages/app/Wallet";
import Chat from "./pages/app/Chat";
import Deliveries from "./pages/app/Deliveries";
import Notifications from "./pages/app/Notifications";
import Analytics from "./pages/app/Analytics";
import Tracking from "./pages/app/Tracking";
import TrackingDetail from "./pages/app/TrackingDetail";
import Settings from "./pages/app/Settings";
import ConfirmEmailChange from "./pages/app/ConfirmEmailChange";
import Transactions from "./pages/app/Transactions";
import Vehicles from "./pages/app/Vehicles";
import VehicleDetail from "./pages/app/VehicleDetail";
import VehicleTracking from "./pages/app/VehicleTracking";
import VehicleAssignments from "./pages/app/VehicleAssignments";
import UsersClients from "./pages/app/UsersClients";
import ClientDashboard from "./pages/app/ClientDashboard";
import UsersPersonal from "./pages/app/UsersPersonal";
import PersonalDashboard from "./pages/app/PersonalDashboard";
import UsersCorporates from "./pages/app/UsersCorporates";
import CorporateDashboard from "./pages/app/CorporateDashboard";
import UsersDrivers from "./pages/app/UsersDrivers";
import UsersStaff from "./pages/app/UsersStaff";
import Support from "./pages/app/Support";
import Trackers from "./pages/app/Trackers";
import Dispatch from "./pages/app/Dispatch";
import Jobs from "./pages/app/Jobs";

// Public Pages
import HackathonNipost from "./pages/public/HackathonNipost";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = useAuth((s) => s.session);
  const { pathname } = useLocation();
  if (!session) return <Navigate to="/auth/login" replace />;

  if (
    session.kind === "personal" &&
    PERSONAL_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (
    session.kind !== "staff" &&
    STAFF_ONLY_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (
    session.kind === "corporate" &&
    (CORPORATE_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/")) ||
      CORPORATE_BLOCKED_EXACT.includes(pathname))
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (
    session.kind === "client" &&
    CLIENT_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (session.kind === "staff") {
    const driverOnly = session.roles.length > 0 && session.roles.every((r) => r === "driver");
    if ((pathname === "/app/dispatch" || pathname.startsWith("/app/dispatch/")) &&
        (driverOnly || !session.roles.some((r) => DISPATCH_CAPABLE_ROLES.includes(r)))) {
      return <Navigate to="/app/dashboard" replace />;
    }
    if ((pathname === "/app/jobs" || pathname.startsWith("/app/jobs/")) && !driverOnly) {
      return <Navigate to="/app/dashboard" replace />;
    }
    if (driverOnly && DRIVER_ONLY_BLOCKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
      return <Navigate to="/app/dashboard" replace />;
    }
    if (driverOnly && DRIVER_ONLY_BLOCKED_SUFFIXES.some((s) => pathname.endsWith(s))) {
      return <Navigate to="/app/dashboard" replace />;
    }
  }
  if (
    SUPERADMIN_ONLY_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/")) &&
    !(session.kind === "staff" && session.roles.includes("superadmin"))
  ) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <>
    <Toaster richColors closeButton position="top-right" />
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/auth/linkedin/callback" element={<LinkedinCallback />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
        <Route path="/auth/handoff" element={<Handoff />} />
        <Route path="/auth/confirm-email" element={<ConfirmEmail />} />
        <Route path="/auth/activate" element={<Activate />} />
      </Route>

      {/* App Routes */}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/app/dashboard" element={<Dashboard />} />
        <Route path="/app/wallet" element={<Wallet />} />
        <Route path="/app/chat" element={<Chat />} />
        <Route path="/app/deliveries" element={<Deliveries />} />
        <Route path="/app/notifications" element={<Notifications />} />
        <Route path="/app/analytics" element={<Analytics />} />
        <Route path="/app/tracking" element={<Tracking />} />
        <Route path="/app/tracking/:parcelId" element={<TrackingDetail />} />
        <Route path="/app/settings" element={<Settings />} />
        <Route path="/app/settings/confirm-email" element={<ConfirmEmailChange />} />
        <Route path="/app/transactions" element={<Transactions />} />
        <Route path="/app/vehicles" element={<Vehicles />} />
        <Route path="/app/vehicles/assignments" element={<VehicleAssignments />} />
        <Route path="/app/vehicles/:vehicleId" element={<VehicleDetail />} />
        <Route path="/app/vehicles/:vehicleId/track" element={<VehicleTracking />} />
        <Route path="/app/users" element={<Navigate to="/app/users/drivers" replace />} />
        <Route path="/app/users/clients" element={<UsersClients />} />
        <Route path="/app/users/clients/:id" element={<ClientDashboard />} />
        <Route path="/app/users/personal" element={<UsersPersonal />} />
        <Route path="/app/users/personal/:id" element={<PersonalDashboard />} />
        <Route path="/app/users/corporates" element={<UsersCorporates />} />
        <Route path="/app/users/corporates/:id" element={<CorporateDashboard />} />
        <Route path="/app/users/drivers" element={<UsersDrivers />} />
        <Route path="/app/users/staff" element={<UsersStaff />} />
        <Route path="/app/support" element={<Support />} />
        <Route path="/app/trackers" element={<Trackers />} />
        <Route path="/app/dispatch" element={<Dispatch />} />
        <Route path="/app/jobs" element={<Jobs />} />
      </Route>

      {/* Public Routes */}
      <Route path="/hackathon-nipost" element={<HackathonNipost />} />

      {/* Root & Fallback */}
      <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
    </>
  );
}
